import type { BoardFaceList } from '@molt/shared';
import { isOptimisticCardId } from './cards';

export const BULK_CARD_SELECTION_LIMIT = 20;

export interface CardSelectionAnchor {
  readonly cardId: string;
  readonly listId: string;
}

export interface CardSelectionResult {
  readonly selectedCardIds: ReadonlySet<string>;
  readonly anchor: CardSelectionAnchor | null;
  readonly notice: string | null;
}

export interface CardSelectionGesture {
  readonly cardId: string;
  readonly listId: string;
  readonly shiftKey?: boolean | undefined;
  readonly metaKey?: boolean | undefined;
  readonly ctrlKey?: boolean | undefined;
}

export type CardSelectionTranslate = (
  key: string,
  values?: Record<string, string | number>,
  count?: number,
) => string;

export function orderSelectedCardIds(
  lists: readonly BoardFaceList[],
  selectedCardIds: ReadonlySet<string>,
): string[] {
  const ordered: string[] = [];
  for (const list of lists) {
    for (const card of list.cards) {
      if (selectedCardIds.has(card.id) && !isOptimisticCardId(card.id)) {
        ordered.push(card.id);
      }
    }
  }
  return ordered;
}

export function selectedCardsInBoardOrder(
  lists: readonly BoardFaceList[],
  selectedCardIds: ReadonlySet<string>,
): BoardFaceList['cards'][number][] {
  const cards: BoardFaceList['cards'][number][] = [];
  for (const list of lists) {
    for (const card of list.cards) {
      if (selectedCardIds.has(card.id) && !isOptimisticCardId(card.id)) {
        cards.push(card);
      }
    }
  }
  return cards;
}

export function selectCardByGesture(
  t: CardSelectionTranslate,
  lists: readonly BoardFaceList[],
  current: ReadonlySet<string>,
  anchor: CardSelectionAnchor | null,
  gesture: CardSelectionGesture,
): CardSelectionResult {
  if (isOptimisticCardId(gesture.cardId)) {
    return {
      selectedCardIds: current,
      anchor,
      notice: t('boardFace.selection.optimistic'),
    };
  }

  const extend = gesture.shiftKey === true;
  const toggle = gesture.metaKey === true || gesture.ctrlKey === true;

  if (extend) {
    if (anchor?.listId !== gesture.listId) {
      return {
        selectedCardIds: new Set([gesture.cardId]),
        anchor: { cardId: gesture.cardId, listId: gesture.listId },
        notice: anchor === null ? null : t('boardFace.selection.crossListRange'),
      };
    }

    const list = lists.find((candidate) => candidate.id === gesture.listId);
    const range = selectRangeInList(list, anchor.cardId, gesture.cardId);
    if (range.length === 0) {
      return {
        selectedCardIds: new Set([gesture.cardId]),
        anchor: { cardId: gesture.cardId, listId: gesture.listId },
        notice: null,
      };
    }

    return capSelection(t, new Set(range), anchor);
  }

  if (toggle) {
    const next = new Set(current);
    if (next.has(gesture.cardId)) {
      next.delete(gesture.cardId);
      return {
        selectedCardIds: next,
        anchor: next.size === 0 ? null : anchor,
        notice: null,
      };
    }

    if (next.size >= BULK_CARD_SELECTION_LIMIT) {
      return {
        selectedCardIds: current,
        anchor,
        notice: t(
          'boardFace.selection.limit',
          { count: BULK_CARD_SELECTION_LIMIT },
          BULK_CARD_SELECTION_LIMIT,
        ),
      };
    }

    next.add(gesture.cardId);
    return {
      selectedCardIds: next,
      anchor: { cardId: gesture.cardId, listId: gesture.listId },
      notice: null,
    };
  }

  return {
    selectedCardIds: new Set([gesture.cardId]),
    anchor: { cardId: gesture.cardId, listId: gesture.listId },
    notice: null,
  };
}

function selectRangeInList(
  list: BoardFaceList | undefined,
  anchorCardId: string,
  targetCardId: string,
): string[] {
  if (list === undefined) return [];
  const ids = list.cards
    .filter((card) => !isOptimisticCardId(card.id))
    .map((card) => card.id);
  const from = ids.indexOf(anchorCardId);
  const to = ids.indexOf(targetCardId);
  if (from === -1 || to === -1) return [];
  const [start, end] = from <= to ? [from, to] : [to, from];
  return ids.slice(start, end + 1);
}

function capSelection(
  t: CardSelectionTranslate,
  next: ReadonlySet<string>,
  anchor: CardSelectionAnchor | null,
): CardSelectionResult {
  if (next.size <= BULK_CARD_SELECTION_LIMIT) {
    return { selectedCardIds: next, anchor, notice: null };
  }
  return {
    selectedCardIds: new Set([...next].slice(0, BULK_CARD_SELECTION_LIMIT)),
    anchor,
    notice: t(
      'boardFace.selection.limit',
      { count: BULK_CARD_SELECTION_LIMIT },
      BULK_CARD_SELECTION_LIMIT,
    ),
  };
}

export function bulkDropNeighbours(
  cards: readonly { id: string }[],
  selectedCardIds: ReadonlySet<string>,
  overId: string,
): { previousCardId: string | null; nextCardId: string | null } {
  const visible = cards.filter((card) => !selectedCardIds.has(card.id));
  const overIndex = visible.findIndex((card) => card.id === overId);
  if (overIndex === -1) {
    return { previousCardId: visible.at(-1)?.id ?? null, nextCardId: null };
  }
  return {
    previousCardId: overIndex === 0 ? null : visible[overIndex - 1]!.id,
    nextCardId: visible[overIndex]?.id ?? null,
  };
}
